import { PriorityBadge } from '@/components/ui/priority-badge'
import type { TaskResponse } from '@/types/task'

// ── Bucket definitions ────────────────────────────────────────────────────────

type TimelineBucket =
  | 'overdue'
  | 'today'
  | 'this_week'
  | 'next_week'
  | 'this_month'
  | 'later'
  | 'no_date'

const BUCKET_LABELS: Record<TimelineBucket, string> = {
  overdue: 'Terlambat',
  today: 'Hari ini',
  this_week: 'Minggu ini',
  next_week: 'Minggu depan',
  this_month: 'Bulan ini',
  later: 'Nanti',
  no_date: 'Tanpa Due Date',
}

const BUCKET_ORDER: TimelineBucket[] = [
  'overdue',
  'today',
  'this_week',
  'next_week',
  'this_month',
  'later',
  'no_date',
]

// ── Date helpers ──────────────────────────────────────────────────────────────

// Parses YYYY-MM-DD as local midnight — avoids UTC off-by-one in positive UTC offsets (e.g. WIB UTC+7).
function parseLocalDate(s: string): Date {
  return new Date(`${s}T00:00:00`)
}

function startOfToday(): Date {
  const d = new Date()
  d.setHours(0, 0, 0, 0)
  return d
}

// Returns Monday of the week containing d (Indonesian work-week convention).
function startOfWeek(d: Date): Date {
  const r = new Date(d)
  r.setHours(0, 0, 0, 0)
  const day = r.getDay() // 0 = Sunday
  r.setDate(r.getDate() + (day === 0 ? -6 : 1 - day))
  return r
}

function getBucket(dueDate: string | null, today: Date): TimelineBucket {
  if (!dueDate) return 'no_date'

  const due = parseLocalDate(dueDate)
  if (due < today) return 'overdue'

  const todayStr = today.toISOString().slice(0, 10)
  if (dueDate === todayStr) return 'today'

  const w0 = startOfWeek(today)
  const w1 = new Date(w0)
  w1.setDate(w1.getDate() + 7)
  const w2 = new Date(w1)
  w2.setDate(w2.getDate() + 7)

  if (due >= w0 && due < w1) return 'this_week'
  if (due >= w1 && due < w2) return 'next_week'

  const m1 = new Date(today.getFullYear(), today.getMonth() + 1, 1)
  if (due < m1) return 'this_month'

  return 'later'
}

function formatDate(date: string): string {
  return new Date(date).toLocaleDateString('id-ID', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  })
}

// ── Component ─────────────────────────────────────────────────────────────────

interface TaskTimelineViewProps {
  tasks: TaskResponse[]
  onTaskClick: (id: string) => void
}

export function TaskTimelineView({ tasks, onTaskClick }: TaskTimelineViewProps) {
  const today = startOfToday()

  // Pre-populate all buckets so .get() never returns undefined.
  const bucketMap = new Map<TimelineBucket, TaskResponse[]>(
    BUCKET_ORDER.map((b) => [b, []])
  )
  for (const task of tasks) {
    bucketMap.get(getBucket(task.due_date, today))!.push(task)
  }

  return (
    <div className="space-y-6">
      {BUCKET_ORDER.map((bucket) => {
        const bucketTasks = bucketMap.get(bucket)!
        if (bucketTasks.length === 0) return null // Collapse empty buckets.

        return (
          <section key={bucket}>
            {/* Section header */}
            <div className="mb-2 flex items-center gap-2">
              <h3 className="text-sm font-semibold">{BUCKET_LABELS[bucket]}</h3>
              <span className="rounded-full bg-muted px-1.5 py-0.5 text-xs font-medium text-muted-foreground">
                {bucketTasks.length}
              </span>
              {bucket === 'overdue' && (
                <span className="text-xs font-medium text-red-600">•</span>
              )}
            </div>

            {/* Task rows */}
            <div className="overflow-hidden rounded-xl border border-border">
              {bucketTasks.map((task, idx) => (
                <button
                  key={task.id}
                  type="button"
                  onClick={() => onTaskClick(task.id)}
                  className={`flex w-full items-center gap-3 px-4 py-2.5 text-left transition-colors hover:bg-secondary ${
                    idx !== 0 ? 'border-t border-border' : ''
                  }`}
                >
                  <span className="flex-1 truncate text-sm font-medium">{task.title}</span>
                  <PriorityBadge priority={task.priority} />
                  <span
                    className={`shrink-0 text-xs ${
                      bucket === 'overdue'
                        ? 'font-medium text-red-600'
                        : bucket === 'today'
                        ? 'font-medium text-orange-500'
                        : 'text-muted-foreground'
                    }`}
                  >
                    {task.due_date ? formatDate(task.due_date) : '—'}
                  </span>
                </button>
              ))}
            </div>
          </section>
        )
      })}
    </div>
  )
}
